<script lang="ts">
  /**
   * The game's mark (since 1.6.0 a part of its own; drawn by `AppFrame` since
   * 1.4.0): a rounded tile (`--ui-radius-sm`) filled with the accent, its
   * deep end at the corner, holding the game's glyph in `--ui-on-accent`
   * (which the contrast gate already holds on both accent stops). Without
   * `mark`, the tile holds the name's first letter.
   *
   * For a page outside the frame that should still say which game this is
   * (a sign-in page, a join link opened cold); inside the frame, `AppFrame`
   * draws it from its `game` prop, and the rail (1.6.0) tops its tabs with
   * it.
   *
   * - `name`: the game's name. The tile's accessible name (`role="img"`), and
   *   the letter when there is no glyph.
   * - `mark`: the glyph, an inline SVG or a character sized in `em` and drawn
   *   in `currentColor`; the tile's type step sizes it.
   * - `size`: `sm` 1.75rem (beside a tab's title), `md` 2.5rem (beside the
   *   name on Home; the default), `lg` 3rem (over the name in the sidebar).
   * - `decorative`: the name is written right beside the tile, so the tile
   *   is hidden from assistive technology (`aria-hidden`) instead of named.
   *
   * Test id: `testid` on the tile; no default (`AppFrame` passes its
   * `testids.mark`, default `ui-app-frame-mark`).
   */
  import type { Snippet } from 'svelte';

  interface Props {
    /** The game's name: the tile's accessible name, and its letter without `mark`. */
    readonly name: string;
    /** The game's glyph, in `currentColor`. */
    readonly mark?: Snippet | undefined;
    readonly size?: 'sm' | 'md' | 'lg';
    /** The name is written beside it: hide the tile instead of naming it. */
    readonly decorative?: boolean;
    readonly testid?: string | undefined;
  }
  const { name, mark, size = 'md', decorative = false, testid }: Props = $props();

  /** The tile's letter when the game gives no glyph. */
  const initial = $derived(Array.from(name.trim())[0] ?? '');
</script>

<!-- The class keeps the name it had inside AppFrame (1.4.0); class names are
     not API. -->
<span
  class="ui-frame-mark ui-frame-mark--{size}"
  role={decorative ? undefined : 'img'}
  aria-label={decorative ? undefined : name}
  aria-hidden={decorative ? 'true' : undefined}
  data-testid={testid}
>
  {#if mark !== undefined}{@render mark()}{:else}{initial}{/if}
</span>

<style>
  /* The glyph is sized by the tile's type step: an `em`-sized SVG or a
     letter scales with it. */
  .ui-frame-mark {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: var(--ui-radius-sm);
    background: linear-gradient(155deg, var(--ui-accent), var(--ui-accent-deep));
    color: var(--ui-on-accent);
    font: var(--ui-text-title);
    line-height: 1;
    letter-spacing: 0;
  }

  .ui-frame-mark--sm {
    width: 1.75rem;
    height: 1.75rem;
    font: var(--ui-text-label);
    font-weight: 800;
    line-height: 1;
  }

  .ui-frame-mark--lg {
    width: 3rem;
    height: 3rem;
  }
</style>
